/** Browser-local date/time text (DESIGN.md §4.5, D-20), and the `datetime-local` input conversion. */

import { EMPTY } from '~/lib/format';

/** Date + time, numeric: the default timestamp shape. */
export const DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
};

/** Date only, numeric (e.g. 10/8/2026). */
export const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
};

export type TimeValue = string | number | Date | null | undefined;

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

export function toDate(value: TimeValue): Date | null {
  if (value === null || value === undefined || value === '') return null;
  // A bare YYYY-MM-DD is a calendar date, not UTC midnight; parsing it as UTC shows the day before west of Greenwich.
  const dateOnly = typeof value === 'string' ? DATE_ONLY.exec(value) : null;
  const date =
    value instanceof Date
      ? value
      : dateOnly
        ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
        : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatLocalTime(
  value: TimeValue,
  options: Intl.DateTimeFormatOptions = DATE_TIME_OPTIONS,
  locale?: string,
): string {
  const date = toDate(value);
  return date ? new Intl.DateTimeFormat(locale, options).format(date) : EMPTY;
}

/** An ISO timestamp as a `datetime-local` input value, in the viewer's time zone. */
export const toLocalInput = (iso: string): string => {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

/** A `datetime-local` input value (viewer's time zone) as an ISO timestamp; empty is `null`. */
export const fromLocalInput = (value: string): string | null =>
  value ? new Date(value).toISOString() : null;

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['day', 86400],
  ['hour', 3600],
  ['minute', 60],
];

/** "just now", "30 seconds ago", "5 minutes ago": how long before `now` a moment was. */
export function formatRelativeTime(
  value: TimeValue,
  now: number = Date.now(),
  locale?: string,
): string {
  const date = toDate(value);
  if (!date) return EMPTY;
  const seconds = Math.max(0, Math.round((now - date.getTime()) / 1000));
  if (seconds < 10) return 'just now';
  const [unit, size] = RELATIVE_UNITS.find(([, size]) => seconds >= size) ?? ['second', 1];
  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(
    -Math.floor(seconds / size),
    unit,
  );
}
